import React from "react";
import { Button, Container } from "react-bootstrap";
import { SXModalBase } from "./SXModal";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { useDispatch } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { closeModal } from "../../features/ui/uiSlice";
import { useTranslation } from "react-i18next";

export const SaveAsModal = (props: {
  projects: Array<{ name: string; guid: string }>;
  isModalOpen: boolean;
  close: () => void;
  onSave: (userData: any) => void;
}) => {
  const [name, setName] = React.useState<string>("");
  const [project, setProject] = React.useState<{ name: string; guid: string } | null>(null);
  const [didHitSave, setDidHitSave] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  // The asset API has shipped a user payload without projects before, so never
  // dereference it directly - an empty list degrades to an empty picker.
  const projects: Array<{ name: string; guid: string }> = Array.isArray(props.projects) ? props.projects : [];

  const footer = (
    <div className="modal-footer-custom">
      <Button
        className="secondary-btn"
        onClick={(e) => {
          dispatch(closeModal("saveAsModal"));
        }}
      >
        {t("cancel_.label")}
      </Button>
      <Button
        className="primary-btn"
        disabled={didHitSave || name.trim() === "" || !project}
        onClick={async () => {
          setDidHitSave(true);
          if (!project) return;
          let guid = await props.onSave({ name, project });
          window.location.href = `/files/${guid}`;
          props.close();
        }}
      >
        {t("save_.label")}
      </Button>
    </div>
  );

  const body = (
    <Container fluid id="sx-table-container">
      <Row>
        <Col>
          <Col xs={{ span: 12, offset: 0 }} className="editfield-label">
            {t("project_.label")}
          </Col>
          <Col xs={{ span: 12, offset: 0 }} className="ps-0 px-0" style={{ height: 32 }}>
            <SXDropdown
              toggleId="project-dropdown-toggle"
              className="sx-dropdown"
              dataTestId="dropdown_project"
              dropDirection="down"
              name="project"
              lang="en"
              localisation={false}
              list={projects.map((proj) => ({ id: proj.guid, name: proj.name }))}
              val={project ? { id: project.guid, name: project.name } : null}
              rawNames
              editableInput
              notSelectedName={t("enter_name_or_select_.label")}
              inputValue={project?.name ?? ""}
              onInputValueChange={(value) => {
                if (value.trim() === "") {
                  setProject(null);
                  return;
                }

                const exactMatch = projects.find((proj) => proj.name === value);
                if (exactMatch) {
                  setProject({ name: exactMatch.name, guid: exactMatch.guid });
                  return;
                }

                setProject({ name: value, guid: "" });
              }}
              onUpdate={(value) => {
                if (value) {
                  setProject({ name: value.name, guid: value.id });
                }
              }}
            />
          </Col>
        </Col>
      </Row>
      <Row className="container-row">
        <Col>
          <Col xs={{ span: 12, offset: 0 }} className="editfield-label">
            {t("name_.label")}
          </Col>
          <Col xs={{ span: 12, offset: 0 }} className="ps-0 px-0" style={{ height: 32 }}>
            <SXEditField
              id="name-input"
              type="text"
              textAlign="left"
              val={name}
              instantupdate
              onUpdate={(value: React.SetStateAction<string>) => setName(value)}
            />
          </Col>
        </Col>
      </Row>
    </Container>
  );

  return (
    <SXModalBase
      header={t("save_as_.label")}
      show={props.isModalOpen}
      body={body}
      footer={footer}
      bodyStyle={{ overflow: "visible", padding: "20px 24px" }}
      className="save-as-modal"
    />
  );
};
